import { useEffect, useMemo, useState } from 'react';

import { InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';

import Cal, { getCalApi } from '@calcom/embed-react';
import { Box, CircularProgress, Typography, SxProps, Theme } from '@mui/material';

const CAL_NAMESPACE = 'backrs-booking';
const STRINGS = appStrings.modules.calendarBookingModal;

type CalendarBookingModalProps = Readonly<{
  open: boolean;
  userId?: number;
  productId: number;
  coachProductId?: number;
  calendarUrl: string;
  contextProductId?: number;
  bookingContext?: string;
  onClose: () => void;
}>;

export const CalendarBookingModal = ({
  open,
  userId,
  productId,
  coachProductId,
  calendarUrl,
  contextProductId,
  bookingContext,
  onClose,
}: CalendarBookingModalProps) => {
  const [calReady, setCalReady] = useState(false);
  const [calLink, setCalLink] = useState(calendarUrl);
  const user = useAppSelector(selectUser);

  // Reset calReady when modal closes
  useEffect(() => {
    if (!open) setCalReady(false);
  }, [open]);

  const calConfig = useMemo(
    () => ({
      layout: 'month_view' as const,
      name: user ? `${user.first_name} ${user.last_name}`.trim() : '',
      email: user?.email ?? '',
      'metadata[user_id]': userId?.toString() ?? '',
      'metadata[product_id]': productId.toString(),
      'metadata[coach_product_id]': coachProductId?.toString() ?? '',
      'metadata[context_product_id]': contextProductId?.toString() ?? '',
      'metadata[booking_context]': bookingContext ?? '',
      theme: 'light' as const,
    }),
    [user, userId, productId, coachProductId, contextProductId, bookingContext],
  );

  useEffect(() => {
    const extractCalLinkFromUrl = () => {
      // Extract calLink from full URL (e.g., "https://app.cal.com/username/event" -> "username/event")
      let calendarLink = calendarUrl;

      try {
        const url = new URL(calendarUrl);

        calendarLink = url.pathname.replace(/^\//, '');
      } catch {
        // If it's not a full URL, use as-is (already a calLink like "username/event")
      }

      return calendarLink;
    };

    const link = extractCalLinkFromUrl();

    setCalLink(link);
  }, [calendarUrl]);

  // Register Cal API linkReady listener
  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const cal = await getCalApi({ namespace: CAL_NAMESPACE });

        cal('ui', { theme: 'light' });
        cal('on', {
          action: 'linkReady',
          callback: () => setCalReady(true),
        });
      } catch (error) {
        console.error('Failed to initialize Cal API:', error);
        setCalReady(true);
      }
    })();
  }, [open]);

  const headerContent = (
    <Box sx={headerContentSx}>
      <Typography variant="text-lg-semibold">{STRINGS.title}</Typography>
      <Typography variant="text-sm-regular" sx={headerDescriptionSx}>
        {STRINGS.description}
      </Typography>
    </Box>
  );

  const bodyContent = (
    <Box sx={bodyContainerSx}>
      {!calReady && (
        <Box sx={loadingOverlaySx}>
          <CircularProgress size={32} />
        </Box>
      )}

      <Cal
        namespace={CAL_NAMESPACE}
        calLink={calLink}
        style={{ width: '100%', height: '100%', overflow: 'auto' }}
        config={calConfig}
      />
    </Box>
  );

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="md"
      headerContent={headerContent}
      bodyContent={bodyContent}
      icon="Calendar"
      hideCircleImage
    />
  );
};

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const headerDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  position: 'relative',
});

const loadingOverlaySx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5,
};
